// frontend/src/components/leads/LeadList.js

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";
import Layout from "../common/Layout";
import LeadTable from "./LeadTable";
import LeadFilters from "./LeadFilters";
import ViewLead from "./ViewLead";
import BulkImport from "../import/BulkImport";
import ExportButton from "../export/ExportButton";
import LoadingSpinner from "../common/LoadingSpinner";
import PastDueList from "../nextActivities/PastDueList";
import "../../styles/LeadPanel.css";

import { QUICK_FILTER_CHIPS } from "./QuickFiltersBar";
import SavedViewsMenu from "./SavedViewsMenu";
import DensityToggle from "./DensityToggle";
import BulkActionsBar from "./BulkActionsBar";
import SearchAutoSuggest from "./SearchAutoSuggest";
import ScheduleExportModal from "./ScheduleExportModal";
import { toSortParam } from "./LeadSortMenu";
import leadPrefsService from "../../services/leadPrefsService";
import { COLUMN_PRESETS } from "./ColumnPresets";
import { STATUS_COLOR_MAP, NEUTRAL_TAB_COLORS } from "../../constants/leadStatusColors";

const PAGE_SIZE = 30;

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "New", label: "New" },
  { key: "Contacted", label: "Contacted" },
  { key: "Qualified", label: "Qualified" },
  { key: "Demo Scheduled", label: "Demo Scheduled" },
  { key: "Demo Completed", label: "Demo Completed" },
  { key: "Won", label: "Won" },
  { key: "Lost", label: "Lost" },
];

const DEFAULT_COLUMNS = {
  dealership: true, salesRep: true, contact: true, status: true,
  products: true, dms: true, demo: true,
};

const OWNER_ROLES = ["Admin", "SM", "Sales Owner", "Sales Manager"];

const DEFAULT_FILTERS = {
  status: "all", search: "", category: "", products: [], dmsProviders: [],
  leadType: "all", leadSource: "all", serviceDashboard: "all", salesRep: "all",
};

function LeadList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const userRole = user?.role || "";
  const ownerCanFilterSalesRep = OWNER_ROLES.includes(userRole);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [countsError, setCountsError] = useState(null);
  const [countsLoading, setCountsLoading] = useState(true);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);
  const [pastDueItems, setPastDueItems] = useState(null);
  const [showPastDueModal, setShowPastDueModal] = useState(false);

  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const requestIdRef = useRef(0);

  const [productCatalog, setProductCatalog] = useState([]);

  const [sortState, setSortState] = useState([]);
  const [activeQuickChips, setActiveQuickChips] = useState([]);
  const [density, setDensity] = useState("comfortable");
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectionMode] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [activeViewId, setActiveViewId] = useState("default");
  const [activeViewName, setActiveViewName] = useState("Default");
  const [activeViewDefault, setActiveViewDefault] = useState(true);
  const [activeViewSnapshot, setActiveViewSnapshot] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [prefsReady, setPrefsReady] = useState(false);

  // 'quick' | 'advance' | null — only one open at a time
  const [activeFilterPanel, setActiveFilterPanel] = useState(null);

  const r6ColumnDefs = [
    { key: "dealership", label: "Dealership" },
    { key: "salesRep", label: "Sales Rep" },
    { key: "contact", label: "Contact" },
    { key: "status", label: "Status" },
    { key: "leadType", label: "Lead Type" },
    { key: "leadSource", label: "Lead Source" },
    { key: "products", label: "Products" },
    { key: "dms", label: "DMS" },
    { key: "activity", label: "Last Activity" },
    { key: "lastContact", label: "Last Contact" },
    { key: "nextContact", label: "Next Contact" },
    { key: "demo", label: "Demo" },
    { key: "contactDate", label: "Contact Date" },
    { key: "opportunity", label: "Opportunity" },
    { key: "email", label: "Email" },
    { key: "mainLine", label: "Main Line" },
    { key: "website", label: "Website" },
    { key: "dealerGroup", label: "Dealer Group" },
    { key: "timeSinceTouch", label: "Time Since Last Touch" },
    { key: "created", label: "Created" },
    { key: "actions", label: "Actions" },
  ];

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [counts, setCounts] = useState({
    total: 0, new_count: 0, contacted_count: 0, qualified_count: 0,
    demo_scheduled_count: 0, demo_completed_count: 0, won_count: 0, lost_count: 0,
  });

  useEffect(() => {
    api.getMasterData("products")
      .then((data) => setProductCatalog(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to load product catalog:", err));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, d, q, a] = await Promise.all([
          leadPrefsService.get(leadPrefsService.KEYS.COLUMNS),
          leadPrefsService.get(leadPrefsService.KEYS.DENSITY),
          leadPrefsService.get(leadPrefsService.KEYS.QUICK_FILTERS),
          leadPrefsService.get(leadPrefsService.KEYS.ACTIVE_VIEW),
        ]);
        if (cancelled) return;
        if (c && typeof c === "object") setVisibleColumns((prev) => ({ ...prev, ...c }));
        if (d) setDensity(d);
        if (Array.isArray(q)) setActiveQuickChips(q);

        let restored = false;
        if (a && a.id) {
          try {
            if (typeof a.id === "string" && a.id.startsWith("preset:")) {
              const key = a.id.slice("preset:".length);
              if (COLUMN_PRESETS[key]) {
                const cols = {};
                for (const def of r6ColumnDefs) {
                  cols[def.key] = COLUMN_PRESETS[key].columns.includes(def.key);
                }
                if (!cancelled) setVisibleColumns(cols);
                setActiveViewId(a.id);
                setActiveViewName(COLUMN_PRESETS[key].label || a.name || key);
                setActiveViewDefault(false);
                restored = true;
              }
            } else if (a.id === "default") {
              setActiveViewId("default");
              setActiveViewName("Default");
              setActiveViewDefault(true);
              setVisibleColumns(DEFAULT_COLUMNS);
              setActiveViewSnapshot({
                  filters:  {},
                  sortState:  [],
                  visibleColumns: DEFAULT_COLUMNS,
                  density:   "comfortable",
                  activeQuickChips:   [],
                });
              restored = true;
            } else {
              const viewsRes = await api.getSavedViews?.();
              const list = Array.isArray(viewsRes?.data) ? viewsRes.data : [];
              const v = list.find((x) => x.id === a.id);
              if (v) {
                if (v.payload?.filters) setFilters((cur) => ({ ...cur, ...v.payload.filters }));
                if (v.payload?.sortState) setSortState(v.payload.sortState);
                if (v.payload?.visibleColumns) setVisibleColumns(v.payload.visibleColumns);
                if (v.payload?.density) setDensity(v.payload.density);
                if (Array.isArray(v.payload?.activeQuickChips)) setActiveQuickChips(v.payload.activeQuickChips);
                setActiveViewId(v.id);
                setActiveViewName(v.name || "");
                setActiveViewDefault(!!v.is_default);
                setActiveViewSnapshot({
                  filters: v.payload?.filters || {},
                  sortState: v.payload?.sortState || [],
                  visibleColumns: v.payload?.visibleColumns || {},
                  density: v.payload?.density || "comfortable",
                  activeQuickChips: v.payload?.activeQuickChips || [],
                });
                restored = true;
              }
            }
          } catch (e) { console.warn("Failed to restore active view:", e); }
        }

        if (!restored) {
          try {
            const viewsRes = await api.getSavedViews?.();
            const list = Array.isArray(viewsRes?.data) ? viewsRes.data : [];
            const userDefault = list.find((x) => x.is_default);
            if (userDefault?.payload) {
              if (userDefault.payload.filters) setFilters((cur) => ({ ...cur, ...userDefault.payload.filters }));
              if (userDefault.payload.sortState) setSortState(userDefault.payload.sortState);
              if (userDefault.payload.visibleColumns) setVisibleColumns(userDefault.payload.visibleColumns);
              if (userDefault.payload.density) setDensity(userDefault.payload.density);
              if (Array.isArray(userDefault.payload.activeQuickChips)) setActiveQuickChips(userDefault.payload.activeQuickChips);
              setActiveViewId(userDefault.id);
              setActiveViewName(userDefault.name || "");
              setActiveViewDefault(true);
              setActiveViewSnapshot({
                filters: userDefault.payload.filters || {},
                sortState: userDefault.payload.sortState || [],
                visibleColumns: userDefault.payload.visibleColumns || {},
                density: userDefault.payload.density || "comfortable",
                activeQuickChips: userDefault.payload.activeQuickChips || [],
              });
              restored = true;
            }
          } catch (e) { console.warn("Failed to load default saved view:", e); }
        }

        if (!restored) {
          setActiveViewId("default");
          setActiveViewName("Default");
          setActiveViewDefault(true);
        }
      } catch (err) {
        console.error("Failed to load lead preferences:", err);
      } finally {
        if (!cancelled) setPrefsReady(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!prefsReady) return;
    const payload = activeViewId
      ? { id: activeViewId, name: activeViewName, isDefault: activeViewDefault }
      : null;
    leadPrefsService
      .set(leadPrefsService.KEYS.ACTIVE_VIEW, payload)
      .catch((e) => console.error("save activeView failed:", e));
  }, [prefsReady, activeViewId, activeViewName, activeViewDefault]);

  const clearActiveView = useCallback(() => {
    setActiveViewId("default");
    setActiveViewName("Default");
    setActiveViewDefault(true);
    setActiveViewSnapshot(null);
    setIsDirty(false);
  }, []);

  const saveDensity = useCallback((next) => {
    setDensity(next);
    leadPrefsService.set(leadPrefsService.KEYS.DENSITY, next)
      .catch((e) => console.error("save density failed:", e));
  }, []);

  const saveQuickChips = useCallback((updater) => {
    setActiveQuickChips((cur) => {
      const next = typeof updater === "function" ? updater(cur) : updater;
      leadPrefsService.set(leadPrefsService.KEYS.QUICK_FILTERS, next)
        .catch((e) => console.error("save quickFilters failed:", e));
      return next;
    });
  }, []);

  const fetchLeads = useCallback(
    async (nextFilters = filters, nextPage = 1, append = false) => {
      const requestId = ++requestIdRef.current;
      if (append) setLoadingMore(true);
      else { setLoading(true); setError(null); }
      try {
        const sortParam = toSortParam(sortState);
        const result = await api.getPaginatedLeads(
          { ...nextFilters, ...(sortParam ? { sort: sortParam } : {}) },
          nextPage, PAGE_SIZE,
        );
        if (requestId !== requestIdRef.current) return;
        const rows = Array.isArray(result?.data) ? result.data : [];
        setLeads((current) => {
          if (!append) return rows;
          const existing = new Set(current.map((lead) => String(lead.lead_id)));
          return [...current, ...rows.filter((lead) => !existing.has(String(lead.lead_id)))];
        });
        setPage(Number(result?.page) || nextPage);
        setHasMore(Boolean(result?.hasMore));
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to load leads:", err);
        setError(err?.response?.data?.error || "Failed to load leads");
        if (!append) setLeads([]);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [filters, sortState],
  );

  const fetchCounts = useCallback(
    async (nextFilters = filters) => {
      setCountsLoading(true);
      setCountsError(null);
      try {
        const result = await api.getLeadStateCounts(nextFilters);
        setCounts({
          total: Number(result?.total) || 0,
          new_count: Number(result?.new_count) || 0,
          contacted_count: Number(result?.contacted_count) || 0,
          qualified_count: Number(result?.qualified_count) || 0,
          demo_scheduled_count: Number(result?.demo_scheduled_count) || 0,
          demo_completed_count: Number(result?.demo_completed_count) || 0,
          won_count: Number(result?.won_count) || 0,
          lost_count: Number(result?.lost_count) || 0,
        });
      } catch (err) {
        console.error("Failed to load lead counts:", err);
        setCountsError(err?.response?.data?.error || "Failed to load lead counts");
      } finally {
        setCountsLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    setPage(1);
    setHasMore(false);
    fetchLeads(filters, 1, false);
    fetchCounts(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortState]);

  useEffect(() => {
    const openLeadId = location.state?.openLeadId;
    if (openLeadId) {
      setSelectedLeadId(openLeadId);
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 500);
    }
  }, [location.state]);

  useEffect(() => {
    const incoming = location.state?.filters;
    if (!incoming || typeof incoming !== "object") return;
    setFilters((current) => {
      const merged = { ...current };
      for (const [k, v] of Object.entries(incoming)) {
        if (k === "_noNext") continue;
        if (v !== undefined && v !== null && v !== "") merged[k] = v;
      }
      return merged;
    });
    setPage(1);
    setHasMore(false);
    setTimeout(() => {
      window.history.replaceState({}, document.title, window.location.pathname);
    }, 500);
  }, [location.state]);

  useEffect(() => {
    if (!activeViewId || !activeViewSnapshot) return;
    const snap = activeViewSnapshot;
    const colsEqual = JSON.stringify(visibleColumns) === JSON.stringify(snap.visibleColumns || {});
    const filtersEqual = JSON.stringify(filters) === JSON.stringify(snap.filters || {});
    const densityEqual = density === (snap.density || "comfortable");
    const sortEqual = JSON.stringify(sortState) === JSON.stringify(snap.sortState || []);
    const chipsEqual = JSON.stringify(activeQuickChips) === JSON.stringify(snap.activeQuickChips || []);
    setIsDirty(!(colsEqual && filtersEqual && densityEqual && sortEqual && chipsEqual));
  }, [activeViewId, activeViewSnapshot, visibleColumns, filters, density, sortState, activeQuickChips]);

  const currentSnapshotObj = () => ({
    filters, sortState, visibleColumns, density, activeQuickChips,
  });

  const handleFilterChange = (newFilters) => {
    setFilters((current) => ({ ...current, ...newFilters }));
  };

  const handleStatusTab = (status) => {
    setFilters((current) => ({ ...current, status }));
  };

  const loadMore = () => {
    if (!loading && !loadingMore && hasMore) {
      fetchLeads(filters, page + 1, true);
    }
  };

  const handleViewLead = (leadId) => setSelectedLeadId(leadId);
  const handleEditLead = (leadId) => navigate(`/leads/${leadId}/edit`);
  const handleBookDemo = (leadId) => navigate(`/leads/${leadId}/demo`);

  const handleLeadUpdated = async () => {
    await Promise.all([fetchLeads(filters, page, false), fetchCounts(filters)]);
  };

  const handleAddActivity = async (leadId, activityData) => {
    const payload = {
      leadId: Number(leadId),
      type: activityData.type,
      description: activityData.description.trim(),
      scheduledDate: activityData.scheduledDate || new Date().toISOString(),
    };
    await api.addActivity(payload);
    await Promise.all([fetchLeads(filters, page, false), fetchCounts(filters)]);
  };

  const handleAddNextActivity = async (leadId, formData) => {
    await api.createNextActivity(Number(leadId), formData);
    await Promise.all([fetchLeads(filters, page, false), fetchCounts(filters)]);
  };

  const handleCompleteNextActivity = async (nextActivityId, remark) => {
    await api.completeNextActivity(nextActivityId, { remark });
    await Promise.all([fetchLeads(filters, page, false), fetchCounts(filters)]);
  };

  const handleSuspendNextActivity = async (nextActivityId, remark) => {
    await api.suspendNextActivity(nextActivityId, { remark });
    await Promise.all([fetchLeads(filters, page, false), fetchCounts(filters)]);
  };

  const handleLeadQuickUpdated = (leadId, newStatus) => {
    setLeads((currentLeads) => {
      const updatedLeads = currentLeads.map((lead) =>
        String(lead.lead_id) === String(leadId) ? { ...lead, status: newStatus } : lead,
      );
      if (filters.status !== "all") {
        return updatedLeads.filter(
          (lead) =>
            String(lead.lead_id) !== String(leadId) || lead.status === filters.status,
        );
      }
      return updatedLeads;
    });
    setCounts((currentCounts) => {
      const lead = leads.find((item) => String(item.lead_id) === String(leadId));
      if (!lead || lead.status === newStatus) return currentCounts;
      const statusCountKeys = {
        New: "new_count", Contacted: "contacted_count", Qualified: "qualified_count",
        "Demo Scheduled": "demo_scheduled_count", "Demo Completed": "demo_completed_count",
        Won: "won_count", Lost: "lost_count",
      };
      const oldKey = statusCountKeys[lead.status];
      const newKey = statusCountKeys[newStatus];
      return {
        ...currentCounts,
        ...(oldKey ? { [oldKey]: Math.max(0, Number(currentCounts[oldKey] || 0) - 1) } : {}),
        ...(newKey ? { [newKey]: Number(currentCounts[newKey] || 0) + 1 } : {}),
      };
    });
  };

  const handleLeadProductsQuickUpdated = (leadId, newProducts) => {
    setLeads((currentLeads) =>
      currentLeads.map((lead) =>
        lead.lead_id === leadId ? { ...lead, products: newProducts } : lead,
      ),
    );
  };

  const handlePanelClose = () => {
    setSelectedLeadId(null);
    fetchLeads(filters, page, false);
    fetchCounts(filters);
  };

  const getStatusCount = (status) => {
    if (status === "all") return counts.total;
    const keys = {
      New: "new_count", Contacted: "contacted_count", Qualified: "qualified_count",
      "Demo Scheduled": "demo_scheduled_count", "Demo Completed": "demo_completed_count",
      Won: "won_count", Lost: "lost_count",
    };
    return Number(counts[keys[status]] || 0);
  };

  const applyViewSnapshot = (snap, view) => {
    if (snap?.filters) setFilters((c) => ({ ...c, ...snap.filters }));
    if (snap?.sortState) setSortState(snap.sortState);
    if (snap?.visibleColumns) setVisibleColumns(snap.visibleColumns);
    if (snap?.density) setDensity(snap.density);
    if (Array.isArray(snap?.activeQuickChips)) setActiveQuickChips(snap.activeQuickChips);
    setPage(1);
    setHasMore(false);
    setOpenMenu(null);
    if (view?.id) {
      setActiveViewId(view.id);
      setActiveViewName(view.name || "");
      setActiveViewDefault(!!view.is_default);
      setActiveViewSnapshot({
        filters: snap?.filters || {},
        sortState: snap?.sortState || [],
        visibleColumns: snap?.visibleColumns || {},
        density: snap?.density || "comfortable",
        activeQuickChips: snap?.activeQuickChips || [],
      });
      setIsDirty(false);
    }
  };

  const applyPreset = (presetKey, columns) => {
    setVisibleColumns(columns);
    setPage(1);
    setHasMore(false);
    const p = COLUMN_PRESETS[presetKey];
    setActiveViewId(`preset:${presetKey}`);
    setActiveViewName(p?.label || presetKey);
    setActiveViewDefault(false);
    setActiveViewSnapshot({
      filters: { ...filters },
      sortState: [...sortState],
      visibleColumns: columns,
      density,
      activeQuickChips: [...activeQuickChips],
    });
    setIsDirty(false);
  };

  const applyDefaultView = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setSortState([]);
    setVisibleColumns({ ...DEFAULT_COLUMNS });
    setDensity("comfortable");
    setActiveQuickChips([]);
    setPage(1);
    setHasMore(false);
    setActiveViewId("default");
    setActiveViewName("Default");
    setActiveViewDefault(true);
    setIsDirty(false);
    setActiveViewSnapshot({
      filters: { ...DEFAULT_FILTERS },
      sortState: [],
      visibleColumns: { ...DEFAULT_COLUMNS },
      density: "comfortable",
      activeQuickChips: [],
    });
  };

  const handleColumnsChange = (next) => setVisibleColumns(next);

  const saveAs = async (name, { activate } = { activate: false }) => {
    let newView = null;
    try {
      const created = await api.createSavedView?.({
        name, entity: "leads", payload: currentSnapshotObj(), isDefault: false,
      });
      newView = created?.data && created.data.id ? created.data : created?.id ? created : null;
    } catch (e) { console.error("Save As failed:", e); return null; }
    if (!newView) {
      try {
        const res = await api.getSavedViews?.();
        const list = Array.isArray(res?.data) ? res.data : [];
        for (let i = list.length - 1; i >= 0; i--) {
          if (list[i].name === name) { newView = list[i]; break; }
        }
      } catch (e) { console.error("Reload after Save As failed:", e); }
    }
    if (activate && newView?.id) {
      setActiveViewId(newView.id);
      setActiveViewName(newView.name || name);
      setActiveViewDefault(!!newView.is_default);
      setActiveViewSnapshot({ ...currentSnapshotObj() });
      setIsDirty(false);
    } else if (activate) {
      setActiveViewName(name);
      setIsDirty(false);
    }
    return newView;
  };

  const updateActive = async ({ activate } = { activate: false }) => {
    if (!activeViewId || String(activeViewId).startsWith("preset:") || activeViewId === "default") return;
    try {
      await api.updateSavedView?.(activeViewId, { payload: currentSnapshotObj() });
      if (activate) {
        setActiveViewSnapshot({ ...currentSnapshotObj() });
        setIsDirty(false);
      }
    } catch (e) { console.error("Update failed:", e); }
  };

  const discardChanges = () => {
    if (activeViewId === "default") { applyDefaultView(); return; }
    if (!activeViewSnapshot) return;
    const snap = activeViewSnapshot;
    setFilters((c) => ({ ...c, ...(snap.filters || {}) }));
    setSortState(snap.sortState || []);
    setVisibleColumns(snap.visibleColumns || DEFAULT_COLUMNS);
    setDensity(snap.density || "comfortable");
    setActiveQuickChips(snap.activeQuickChips || []);
    setIsDirty(false);
  };

  const activeFilterCount = (() => {
    let n = 0;
    if (filters.leadType && filters.leadType !== "all") n++;
    if (filters.leadSource && filters.leadSource !== "all") n++;
    if (filters.serviceDashboard && filters.serviceDashboard !== "all") n++;
    if (filters.salesRep && filters.salesRep !== "all") n++;
    if (filters.category) n++;
    if (filters.nextContact && filters.nextContact !== "all") n++;
    if (Array.isArray(filters.dmsProviders) && filters.dmsProviders.length) n++;
    if (Array.isArray(filters.products) && filters.products.length) n++;
    return n;
  })();

  const allSelected = leads.length > 0 && selectedIds.size >= leads.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < leads.length;

  const handleClearAllFilters = () => {
    saveQuickChips([]);
    setActiveQuickChips([]);
    setFilters({ ...DEFAULT_FILTERS });
    setPage(1);
    setHasMore(false);
  };

  const handleQuickChipToggle = (chip) => {
    saveQuickChips((cur) =>
      cur.includes(chip.key)
        ? cur.filter((k) => k !== chip.key)
        : [...cur, chip.key],
    );
    setFilters((cur) => ({ ...cur, ...chip.filter }));
    setPage(1);
    setHasMore(false);
  };

  const handleQuickChipRemove = (key) => {
    saveQuickChips((cur) => cur.filter((k) => k !== key));
    const chip = QUICK_FILTER_CHIPS.find((c) => c.key === key);
    if (chip) {
      const cleared = {};
      for (const k of Object.keys(chip.filter)) cleared[k] = "all";
      setFilters((cur) => ({ ...cur, ...cleared }));
    }
  };

  return (
    <Layout title="My Leads" subtitle="Manage and track your leads">
      <div className="my-leads-page">
        {/* Row 1 — Status tabs + count */}
        <div className="partner-leads-header">
          <div className="partner-status-tabs">
            {STATUS_TABS.map((tab) => {
              const isActive = filters.status === tab.key;
              const slug = tab.key === "all"
                ? "all"
                : tab.key.toLowerCase().replace(/\s+/g, "-");

              return (
                <button
                  type="button"
                  key={tab.key}
                  className={`partner-status-tab status-tab-${slug} ${isActive ? "active" : ""}`}
                  onClick={() => handleStatusTab(tab.key)}
                >
                  <span>{tab.label}</span>
                  <strong>{countsLoading ? "…" : getStatusCount(tab.key)}</strong>
                </button>
              );
            })}
          </div>
          <div className="my-leads-header-count">
            Showing <strong>{leads.length}</strong> of <strong>{counts.total}</strong> leads
          </div>
        </div>

        {/* Row 2 — single line: search + actions + filter icons */}
        <div className="r6-search-actions-row">
          <SearchAutoSuggest
            value={filters.search}
            onChange={(v) => handleFilterChange({ search: v })}
            onPick={(lead) => setSelectedLeadId(lead.lead_id)}
            className="r6-search-grow"
          />

          <label className="r6-select-all-icon" title={allSelected ? "Clear selection" : "Select all leads"}>
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = someSelected; }}
              onChange={() => {
                if (selectedIds.size > 0) setSelectedIds(new Set());
                else setSelectedIds(new Set(leads.map((l) => String(l.lead_id))));
              }}
            />
          </label>

          <button
            type="button"
            className={`r6-filter-icon-btn ${activeFilterPanel === "quick" ? "is-open" : ""} ${activeQuickChips.length > 0 ? "has-active" : ""}`}
            title="Quicker filters"
            aria-label="Quicker filters"
            onClick={() =>
              setActiveFilterPanel((cur) => (cur === "quick" ? null : "quick"))
            }
          >
            <i className="fas fa-bolt" />
          </button>

          <button
            type="button"
            className={`r6-filter-icon-btn ${activeFilterPanel === "advance" ? "is-open" : ""} ${activeFilterCount > 0 ? "has-active" : ""}`}
            title="Advance filter"
            aria-label="Advance filter"
            onClick={() =>
              setActiveFilterPanel((cur) => (cur === "advance" ? null : "advance"))
            }
          >
            <i className="fas fa-sliders-h" />
            {activeFilterCount > 0 && (
              <span className="r6-filter-badge">{activeFilterCount}</span>
            )}
          </button>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleClearAllFilters}
          >
            <i className="fas fa-undo" /> Clear
          </button>

          <ExportButton getExportData={() => api.exportLeads(filters)} />

          {ownerCanFilterSalesRep && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowBulkImport(true)}
            >
              <i className="fas fa-file-upload" /> Bulk Import
            </button>
          )}

          <SavedViewsMenu
            currentSnapshot={currentSnapshotObj()}
            open={openMenu === "views"}
            onOpenChange={(v) => setOpenMenu(v ? "views" : null)}
            activeViewId={activeViewId}
            isDirty={isDirty}
            columnsDefs={r6ColumnDefs}
            visibleColumns={visibleColumns}
            onApply={(snap, view) => applyViewSnapshot(snap, view)}
            onApplyPreset={applyPreset}
            onApplyDefault={applyDefaultView}
            onColumnsChange={handleColumnsChange}
            onSaveAs={saveAs}
            onUpdate={updateActive}
            onDiscard={discardChanges}
            onDefaultChanged={({ id, name, isDefault }) => {
              if (id === activeViewId) {
                setActiveViewName(name || "");
                setActiveViewDefault(!!isDefault);
              }
            }}
            onRenamed={({ id, name }) => {
              if (id === activeViewId) setActiveViewName(name || "");
            }}
            onDeleted={(id) => {
              if (id === activeViewId) clearActiveView();
            }}
            defaultSuggestion="My leads"
          />

          <DensityToggle value={density} onChange={saveDensity} />

          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => navigate("/leads/add")}
          >
            <i className="fas fa-plus" /> Add Lead
          </button>
        </div>

        {/* Row 3 — panel (only one open at a time) */}
        {activeFilterPanel === "quick" && (
          <div className="r6-quick-panel">
            {QUICK_FILTER_CHIPS.map((chip) => {
              const active = activeQuickChips.includes(chip.key);
              return (
                <button
                  key={chip.key}
                  type="button"
                  className={`r6-quick-chip ${active ? "is-active" : ""}`}
                  onClick={() => handleQuickChipToggle(chip)}
                >
                  {chip.label}
                  {active && <i className="fas fa-times r6-quick-chip-x" />}
                </button>
              );
            })}
            {activeQuickChips.length > 0 && (
              <button
                type="button"
                className="r6-quick-clear"
                onClick={() => {
                  saveQuickChips([]);
                  setFilters((cur) => ({
                    ...cur, status: "all", leadType: "all", leadSource: "all",
                    serviceDashboard: "all", nextContact: "all",
                  }));
                }}
              >
                Clear chips
              </button>
            )}
          </div>
        )}

        {activeFilterPanel === "advance" && (
          <LeadFilters
            onFilterChange={handleFilterChange}
            activeStatus={filters.status}
            userRole={userRole}
            externalFilters={filters}
            hideSearch={true}
            hideStatus={true}
          />
        )}

        {error && (
          <div className="error-message" role="alert">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}
        {countsError && (
          <div className="error-message" role="alert">{countsError}</div>
        )}

        <div className={`my-leads-data-scroll r6-density-${density}`}>
          {loading && leads.length === 0 ? (
            <div className="my-leads-table-loading">
              <LoadingSpinner message="Loading leads..." />
            </div>
          ) : (
            <div className="leads-table-wrapper">
              <LeadTable
                leads={leads}
                onBookDemo={handleBookDemo}
                onView={handleViewLead}
                onEdit={handleEditLead}
                onLeadQuickUpdated={handleLeadQuickUpdated}
                onLeadProductsQuickUpdated={handleLeadProductsQuickUpdated}
                onAddActivity={handleAddActivity}
                onAddNextActivity={handleAddNextActivity}
                onCompleteNextActivity={handleCompleteNextActivity}
                onSuspendNextActivity={handleSuspendNextActivity}
                userRole={userRole}
                visibleColumns={visibleColumns}
                productCatalog={productCatalog}
                sortState={sortState}
                onSortChange={setSortState}
                selectionMode={selectionMode}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
              />
            </div>
          )}

          {loading && leads.length > 0 && (
            <div className="lead-list-refreshing" aria-live="polite">
              <i className="fas fa-spinner fa-spin"></i> Refreshing leads...
            </div>
          )}

          {hasMore ? (
            <div className="my-leads-load-more">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <><i className="fas fa-spinner fa-spin"></i> Loading...</>
                ) : (
                  <><i className="fas fa-chevron-down"></i> Load More</>
                )}
              </button>
            </div>
          ) : leads.length > 0 ? (
            <div className="my-leads-end-message">Showing all loaded leads</div>
          ) : null}
        </div>
      </div>

      {showBulkImport && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: "900px" }}>
            <BulkImport
              onComplete={() => {
                setShowBulkImport(false);
                fetchLeads(filters, 1, false);
                fetchCounts(filters);
              }}
              onCancel={() => setShowBulkImport(false)}
            />
          </div>
        </div>
      )}

      {selectedLeadId && (
        <ViewLead
          leadId={selectedLeadId}
          embedded
          onClose={handlePanelClose}
          onUpdated={handleLeadUpdated}
          focusSection={location.state?.focusSection || null}
          focusNextActivityId={location.state?.nextActivityId || null}
        />
      )}

      {showPastDueModal && (
        <PastDueList
          items={pastDueItems || []}
          onClose={() => setShowPastDueModal(false)}
          onChanged={async () => {
            const res = await api.getPastDueNextActivities();
            setPastDueItems(Array.isArray(res?.data) ? res.data : []);
            await Promise.all([fetchLeads(filters, page, false), fetchCounts(filters)]);
          }}
        />
      )}

      <BulkActionsBar
        selectedCount={selectedIds.size}
        canManage={OWNER_ROLES.includes(userRole)}
        onAssign={() => {
          const rep = window.prompt("Assign selected leads to rep ID:");
          if (!rep) return;
          Promise.all(
            [...selectedIds].map((id) => api.updateLead(id, { salesRepId: Number(rep) })),
          ).then(() => { setSelectedIds(new Set()); fetchLeads(filters, page, false); });
        }}
        onStatus={() => {
          const status = window.prompt("Set status to (New, Contacted, Qualified, etc.):");
          if (!status) return;
          Promise.all([...selectedIds].map((id) => api.updateLead(id, { status })))
            .then(() => {
              setSelectedIds(new Set());
              fetchLeads(filters, page, false);
              fetchCounts(filters);
            });
        }}
        onLogActivity={() => {
          const note = window.prompt("Activity note:");
          if (!note) return;
          Promise.all(
            [...selectedIds].map((id) =>
              api.addActivity({
                leadId: Number(id), type: "Note Added", description: note,
                scheduledDate: new Date().toISOString(),
              }),
            ),
          ).then(() => { setSelectedIds(new Set()); fetchLeads(filters, page, false); });
        }}
        onAddNextContact={() => {}}
        onExport={() => { console.log("Export selected:", { filters, selectedIds: [...selectedIds] }); }}
        onDeselect={() => setSelectedIds(new Set())}
      />

      <ScheduleExportModal
        open={showScheduleModal}
        currentFilters={filters}
        defaultName={`My ${filters.status !== "all" ? filters.status : ""} leads`.trim()}
        onClose={() => setShowScheduleModal(false)}
        onCreated={() => setShowScheduleModal(false)}
      />
    </Layout>
  );
}

export default LeadList;